/**
 * Open third-party checkout widgets when present on the storefront.
 * Falls back to false so callers can redirect to Shopify /checkout.
 */

const LOG_PREFIX = "[third-party-checkout]";

function logCheckout(...args) {
  try {
    console.log(LOG_PREFIX, ...args);
  } catch (_) {
    /* ignore */
  }
}

function warnCheckout(...args) {
  try {
    console.warn(LOG_PREFIX, ...args);
  } catch (_) {
    /* ignore */
  }
}

/** Snapshot Razorpay Magic Checkout presence for debugging. */
export function getRazorpayMagicDebugSnapshot() {
  const configEl = document.getElementById("RazorpayMagicCheckoutConfig");
  const container = document.getElementById("magic-checkout-data-container");
  const magicScripts = Array.from(
    document.querySelectorAll(
      'script[src*="magic-plugins.razorpay"], script[src*="razorpay"]'
    )
  ).map((s) => s.getAttribute("src"));

  let configText = null;
  try {
    configText = configEl ? configEl.textContent?.slice(0, 500) : null;
  } catch (_) {
    /* ignore */
  }

  return {
    hasMagicContainer: !!container,
    hasConfigScript: !!configEl,
    configPreview: configText,
    magicScripts,
    windowKeys: [
      "Razorpay",
      "RazorpayMagicCheckout",
      "MagicCheckout",
      "magicCheckout",
      "rzpMagicCheckout",
      "RZPMAGIC",
      "Shopify",
    ].reduce((acc, key) => {
      acc[key] = typeof window[key];
      return acc;
    }, {}),
  };
}

export function tryOpenShiprocketCheckout(clickEvent) {
  const buyCart = window?.shiprocketCheckoutEvents?.buyCart;
  if (typeof buyCart !== "function") return false;
  try {
    buyCart(clickEvent);
    return true;
  } catch (error) {
    console.error("[third-party-checkout] Shiprocket buyCart failed:", error);
    return false;
  }
}

/**
 * Breeze (Juspay) checkout popup. Their store script intercepts theme
 * checkout clicks and calls window.breeze.startCheckout() with no args.
 */
export function tryOpenBreezeCheckout() {
  const startCheckout = window?.breeze?.startCheckout;
  if (typeof startCheckout !== "function") return false;
  try {
    startCheckout();
    return true;
  } catch (error) {
    console.error("[third-party-checkout] Breeze startCheckout failed:", error);
    return false;
  }
}

function describeCheckoutEl(el) {
  if (!el) return null;
  try {
    const style = window.getComputedStyle(el);
    const parent = el.parentElement;
    const parentStyle = parent ? window.getComputedStyle(parent) : null;
    return {
      tag: el.tagName,
      name: el.getAttribute("name"),
      href: el.getAttribute("href"),
      className: el.className,
      disabledClass: el.classList.contains("disabled"),
      disabledAttr: el.getAttribute("disabled") != null,
      ariaDisabled: el.getAttribute("aria-disabled"),
      inWeuDrawer: !!el.closest(".weu-custom-cart-drawer"),
      inMiniCart: !!el.closest(".mini-cart-content, .cardraw, .mini-cart"),
      display: style.display,
      visibility: style.visibility,
      pointerEvents: style.pointerEvents,
      parentDisplay: parentStyle?.display,
      parentPointerEvents: parentStyle?.pointerEvents,
      offsetParent: el.offsetParent ? el.offsetParent.tagName : null,
      rect: (() => {
        try {
          const r = el.getBoundingClientRect();
          return { w: r.width, h: r.height, top: r.top, left: r.left };
        } catch (_) {
          return null;
        }
      })(),
    };
  } catch (error) {
    return { error: String(error?.message || error) };
  }
}

/**
 * Click the theme’s native checkout control so Razorpay Magic Checkout
 * (`magic-shopify.js`) / similar apps still run.
 *
 * This theme pattern:
 *   <checkout-button name="checkout">
 *     <a name="checkout" class="js-cart-btn-checkout" href="/checkout">
 *
 * Temporarily clears theme `disabled` (terms checkbox) and pointer-events locks
 * from our drawer takeover hide styles. Skips WeUpsell’s own button.
 */
export function tryClickThemeCheckoutButton() {
  if (typeof document === "undefined") return false;

  logCheckout("tryClickThemeCheckoutButton:start", getRazorpayMagicDebugSnapshot());

  const selectors = [
    "checkout-button a.js-cart-btn-checkout",
    "checkout-button a[name='checkout']",
    "checkout-button a[href*='checkout']",
    ".mini-cart-btns a.js-cart-btn-checkout",
    ".mini-cart-footer a.js-cart-btn-checkout",
    "a.js-cart-btn-checkout[href*='checkout']",
    "a[name='checkout'][href*='checkout']",
    "button.js-cart-btn-checkout",
    "button[name='checkout']",
    ".mini-cart-footer a[href*='/checkout']",
    ".mini-cart-btns a[href*='/checkout']",
    ".js-cart-btn-checkout",
    "form[action*='/cart'] [name='checkout']",
    "a[href='/checkout'][name='checkout']",
  ];

  let themeLink = null;
  let matchedSelector = null;
  for (let i = 0; i < selectors.length; i++) {
    const nodes = document.querySelectorAll(selectors[i]);
    for (let j = 0; j < nodes.length; j++) {
      const el = nodes[j];
      if (!el || el.closest(".weu-custom-cart-drawer")) continue;
      themeLink = el;
      matchedSelector = selectors[i];
      break;
    }
    if (themeLink) break;
  }

  logCheckout("themeLink match", {
    matchedSelector,
    el: themeLink,
    describe: describeCheckoutEl(themeLink),
  });

  if (!themeLink) {
    warnCheckout("No theme checkout control found");
    return false;
  }

  const hadDisabledClass = themeLink.classList.contains("disabled");
  const hadDisabledAttr = themeLink.getAttribute("disabled") != null;
  const hadAriaDisabled = themeLink.getAttribute("aria-disabled") === "true";
  if (hadDisabledClass) themeLink.classList.remove("disabled");
  if (hadDisabledAttr) themeLink.removeAttribute("disabled");
  if (hadAriaDisabled) themeLink.setAttribute("aria-disabled", "false");

  // Our cart-drawer takeover sets pointer-events:none / display:none on theme mini-cart.
  const styleRestores = [];
  let node = themeLink;
  while (node && node !== document.documentElement) {
    try {
      const pe = node.style && node.style.getPropertyValue("pointer-events");
      if (pe === "none") {
        styleRestores.push({
          el: node,
          prop: "pointer-events",
          value: pe,
          priority: "important",
        });
        node.style.removeProperty("pointer-events");
      }
      const disp = node.style && node.style.getPropertyValue("display");
      if (disp === "none") {
        styleRestores.push({
          el: node,
          prop: "display",
          value: disp,
          priority: "important",
        });
        node.style.setProperty("display", "block", "important");
      }
      const vis = node.style && node.style.getPropertyValue("visibility");
      if (vis === "hidden") {
        styleRestores.push({
          el: node,
          prop: "visibility",
          value: vis,
          priority: "important",
        });
        node.style.setProperty("visibility", "visible", "important");
      }
    } catch (_) {
      /* ignore */
    }
    node = node.parentElement;
  }

  logCheckout("pre-click describe", describeCheckoutEl(themeLink));
  logCheckout("style restores queued", styleRestores.length);

  let clicked = false;
  try {
    // 1) Native click (most handlers)
    if (typeof themeLink.click === "function") {
      logCheckout("calling themeLink.click()");
      themeLink.click();
      clicked = true;
    }

    // 2) Bubbled MouseEvent — some apps listen only for this path
    try {
      const evt = new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
        buttons: 1,
      });
      logCheckout("dispatching MouseEvent click", {
        defaultPreventedBefore: evt.defaultPrevented,
      });
      const cancelled = !themeLink.dispatchEvent(evt);
      logCheckout("MouseEvent result", {
        cancelled,
        defaultPrevented: evt.defaultPrevented,
      });
      clicked = true;
    } catch (evtErr) {
      warnCheckout("MouseEvent dispatch failed", evtErr);
    }

    // 3) Visible body proxy — Razorpay may ignore clicks on display:none trees
    try {
      const href = themeLink.getAttribute("href") || "/checkout";
      const proxy = document.createElement("a");
      proxy.setAttribute("href", href);
      proxy.setAttribute("name", themeLink.getAttribute("name") || "checkout");
      proxy.className =
        themeLink.className ||
        "js-cart-btn-checkout btn gradient-theme";
      proxy.textContent = "Checkout";
      proxy.setAttribute("data-weu-checkout-proxy", "1");
      proxy.style.cssText =
        "position:fixed;left:0;top:0;width:1px;height:1px;opacity:0.01;z-index:2147483647;";
      document.body.appendChild(proxy);
      logCheckout("proxy link created, clicking", {
        href,
        className: proxy.className,
      });
      proxy.click();
      setTimeout(() => {
        try {
          proxy.remove();
        } catch (_) {
          /* ignore */
        }
      }, 1500);
      clicked = true;
    } catch (proxyErr) {
      warnCheckout("proxy click failed", proxyErr);
    }
  } catch (error) {
    console.error(
      "[third-party-checkout] theme checkout click failed:",
      error
    );
  } finally {
    // Delay restore so sync Razorpay handlers can finish first.
    setTimeout(() => {
      try {
        if (hadDisabledClass) themeLink.classList.add("disabled");
        if (hadDisabledAttr) themeLink.setAttribute("disabled", "");
        if (hadAriaDisabled) themeLink.setAttribute("aria-disabled", "true");
        for (let i = 0; i < styleRestores.length; i++) {
          const item = styleRestores[i];
          try {
            item.el.style.setProperty(
              item.prop,
              item.value || "none",
              item.priority || "important"
            );
          } catch (_) {
            /* ignore */
          }
        }
        logCheckout("restored theme link / hide styles");
      } catch (restoreErr) {
        warnCheckout("restore failed", restoreErr);
      }
    }, 800);
  }

  logCheckout("tryClickThemeCheckoutButton:done", { clicked });
  return clicked;
}

/**
 * GoKwik / KwikCheckout — try custom trigger, then SDK, then click handlers.
 */
export function tryOpenGokwikCheckout(clickEvent, { preferBuyNow = false } = {}) {
  const event = clickEvent || undefined;

  const tryCall = (fn, label, args = []) => {
    if (typeof fn !== "function") return false;
    try {
      fn(...args);
      return true;
    } catch (error) {
      console.error(`[third-party-checkout] GoKwik ${label} failed:`, error);
      return false;
    }
  };

  if (
    tryCall(
      window?.triggerGokwikCustomCheckout,
      "triggerGokwikCustomCheckout",
      event != null ? [event] : []
    )
  ) {
    return true;
  }

  const initCheckout = window?.gokwikSdk?.initCheckout;
  const merchantInfo = window?.merchantInfo;
  if (typeof initCheckout === "function" && merchantInfo) {
    if (tryCall(initCheckout, "gokwikSdk.initCheckout", [merchantInfo])) {
      return true;
    }
  }

  if (preferBuyNow) {
    if (
      tryCall(window?.gokwikBuyNowClickFn, "gokwikBuyNowClickFn", [
        event || new Event("click"),
      ])
    ) {
      return true;
    }
  }

  if (
    tryCall(window?.gokwikCheckoutClickFn, "gokwikCheckoutClickFn", [
      event || new Event("click"),
    ])
  ) {
    return true;
  }

  if (
    !preferBuyNow &&
    tryCall(window?.gokwikBuyNowClickFn, "gokwikBuyNowClickFn", [
      event || new Event("click"),
    ])
  ) {
    return true;
  }

  return false;
}
